/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { useContext } from 'react';

import createLoadableComponent from 'js/lib/createLoadableComponent';

import { FullscreenDialog, Grid, InlineNotification, PageGridContainer, Skeleton } from '@coursera/cds-core';

import { InviteToApplyContext } from 'bundles/account-profile/components/recruiting-profile-viewer/InviteToApplyProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';

import _t from 'i18n!nls/account-profile';

const styles = {
  headingGroup: css`
    padding-bottom: 0;
  `,
  withMarginBottom: () => css`
    margin-bottom: var(--cds-spacing-400);
  `,
};

type InviteToApplyLoaderProps = {
  open: boolean;
  onClose: () => void;
};
const InviteToApplyLoader = ({ open, onClose }: InviteToApplyLoaderProps) => {
  const { Header, HeadingGroup, Content } = FullscreenDialog;
  return (
    <FullscreenDialog open={open} onClose={onClose} contentWidth="45%">
      <Header />
      <HeadingGroup heading={_t('Invite 1 candidate to apply')} css={styles.headingGroup} />
      <Content>
        <div css={styles.withMarginBottom}>
          <Skeleton height={60} width={350} />
        </div>
        <div css={styles.withMarginBottom}>
          <Skeleton height={30} width={50} />
          <Skeleton height={30} width={100} />
          <Skeleton height={70} />
        </div>
        <div css={styles.withMarginBottom}>
          <Skeleton height={50} width={200} />
          <Skeleton height={30} />
          <Skeleton height={30} width={100} />
          <Skeleton height={70} />
        </div>
      </Content>
    </FullscreenDialog>
  );
};

const LoadableInvitationFlow = createLoadableComponent(
  () => import('bundles/employability-common'),
  InviteToApplyLoader
);

const InviteToApply = () => {
  const { profileState, isRecruiter } = useContext(ProfileContext);
  const { showInviteToApplyModal, setShowInviteToApplyModal } = useContext(InviteToApplyContext);
  if (!isRecruiter || !profileState || !profileState.externalId) return null;

  return (
    <PageGridContainer maxWidth="lg">
      <Grid item xs={12} sm>
        <LoadableInvitationFlow
          filteredCandidateCount={1}
          selectedCandidates={[profileState.externalId]}
          organizationId={profileState.recruitingOrganizationOfViewer?.id}
          isOpen={showInviteToApplyModal}
          onClose={() => setShowInviteToApplyModal(false)}
          // Props for InviteToApplyLoader
          open={showInviteToApplyModal}
        />
      </Grid>
    </PageGridContainer>
  );
};

const ErrorBoundInviteToApply = () => {
  return (
    <ErrorBoundaryWithTagging
      componentName="InviteToApply"
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t('There has been an error in the application invite flow. Our development team will be alerted.')}
        </InlineNotification>
      }
    >
      <InviteToApply />
    </ErrorBoundaryWithTagging>
  );
};

export default ErrorBoundInviteToApply;
